/*
 * TunePad
 *
 * Michael S. Horn
 * Northwestern University
 * michael-horn@northwestern.edu
 *
 * This project was funded by the National Science Foundation (grant DRL-1612619).
 * Any opinions, findings and conclusions or recommendations expressed in this
 * material are those of the author(s) and do not necessarily reflect the views
 * of the National Science Foundation (NSF).
 */
import { MusicTrace } from '../../audio';


/** Timeline has three different view options */
export const TimelineViews = [ "midi", "waveform", "score", "canvas", "none" ];


/**
 * Timeline can show midi piano roll or waveform view of audio
 * 
 * <tunepad-timeline
 *     playhead = "0"           // location of the playhead in beats
 *     theme = "dark | light"   // default is dark
 *     instrument = "piano"     // changes the color of the visualization
 *     view = "waveform">       // visualization "midi", "waveform", "score", "none"
 */
export class Timeline extends HTMLElement {

    static readonly ELEMENT = "tunepad-timeline";

    static observedAttributes = [
        "playhead",    // location of the playhead in beats
        "theme",       // "dark" | "light"
        "instrument",  // changes the color of the waveform or trace (TimelineInstruments)
        "view"         // "midi", "waveform", "score", "canvas", "none"
    ];


    /** view attribute (midi, waveform, score) */
    private view : any;
    private get isCanvas() { return this.view && this.view.ELEMENT === 'timeline-canvas'; }
    private get isWaveform() { return this.view && this.view.ELEMENT === 'timeline-waveform' }
    private get isMidiRoll() { return this.view && this.view.ELEMENT === 'timeline-midi'; }
    private get isScore() { return this.view && this.view.ELEMENT === 'timeline-score'; }

    private trace : MusicTrace = MusicTrace.emptyTrace();


    constructor() {
        super();
    }

    connectedCallback() {
        if (!this.view) this.switchView('midi');
    }

    disconnectedCallback() { }


    attributeChangedCallback(name : string, oldValue : string, newValue : string) {
        if (oldValue === newValue) return;
        if ([ 'playhead', 'theme', 'instrument' ].includes(name)) {
            this.view?.setAttribute(name, newValue);
        }
        else if (name === 'view' && TimelineViews.includes(newValue)) {
            this.switchView(newValue);
        }
    }


    public setTrace(t : MusicTrace, force : boolean = false) : void {
        this.trace = t;
        this.view?.setTrace(t, force);
    }


    private switchView(v : string) {
        if (v === 'midi' && !this.isMidiRoll) {
            this.view = document.createElement('timeline-midi');
        }
        else if (v === 'waveform' && !this.isWaveform) {
            this.view = document.createElement('timeline-waveform');
        }
        else if (v === 'score' && !this.isScore) {
            this.view = document.createElement('timeline-score');
        }
        else if (v === 'canvas' && !this.isCanvas) {
            this.view = document.createElement('timeline-canvas');
        }
        else if (v === 'none') {
            this.innerHTML = '';
            this.view = undefined;
            return;
        }
        else if (this.view) {
            return;
        }
        else {
            this.view = document.createElement('timeline-midi');
        }
        if (this.hasAttribute('playhead')) this.view.setAttribute('playhead', this.getAttribute('playhead')!);
        if (this.hasAttribute('instrument')) this.view.setAttribute('instrument', this.getAttribute('instrument')!);
        if (this.hasAttribute('theme')) this.view.setAttribute('theme', this.getAttribute('theme')!);
        this.innerHTML = '';
        this.appendChild(this.view);
        this.view.setTrace(this.trace);
    }
}
